<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="900"
    scrollable
  >
    <v-card>
      <v-card-title class="mb-1 primary--text">
        Prenotazione n. {{ prenotazione.numero }}
        <v-spacer />
        <StatoBadge :stato="prenotazione.stato" class="mr-8" />
        <v-btn icon color="grey" title="Stampa PDF" @click="stampa_pdf">
          <v-icon>mdi-printer</v-icon>
        </v-btn>
        <v-btn icon color="grey" @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-subtitle class="pb-2">
        <b>{{ prenotazione.cliente }}</b
        ><br />
        del {{ prenotazione.data }}
      </v-card-subtitle>

      <v-divider />

      <v-card-text class="pt-4">
        <!-- Data ritiro prevista -->
        <div class="d-flex align-center mb-4">
          <v-icon small class="mr-2" color="grey">mdi-calendar-clock</v-icon>
          <span class="body-2">
            <span class="grey--text mr-1">Ritiro previsto:</span>
            <span v-if="prenotazione.data_ritiro">{{
              prenotazione.data_ritiro
            }}</span>
            <span v-else class="font-italic grey--text">non specificato</span>
          </span>
          <v-spacer />
          <v-menu
            v-if="is_modificabile"
            v-model="menu_ritiro"
            offset-y
            left
            :close-on-content-click="false"
            max-width="380"
          >
            <template #activator="{ on }">
              <v-btn icon x-small v-on="on" @click="init_ritiro_picker">
                <v-icon x-small>mdi-pencil</v-icon>
              </v-btn>
            </template>
            <v-card width="360">
              <v-card-title class="subtitle-2 py-2 primary--text"
                >Data ritiro prevista</v-card-title
              >
              <v-card-text class="pb-0">
                <v-row no-gutters>
                  <v-col cols="7" class="pr-2">
                    <v-dialog
                      ref="dialog_ritiro_date"
                      v-model="modal_ritiro_date"
                      :return-value.sync="ritiro_date"
                      persistent
                      width="290px"
                    >
                      <template #activator="{ on, attrs }">
                        <v-text-field
                          v-model="ritiro_date_label"
                          label="Data"
                          prepend-icon="mdi-calendar"
                          readonly
                          dense
                          v-bind="attrs"
                          v-on="on"
                        />
                      </template>
                      <v-date-picker
                        v-model="ritiro_date"
                        scrollable
                        locale="it-it"
                        :min="min_ritiro_date"
                      >
                        <v-spacer />
                        <v-btn
                          text
                          color="primary"
                          @click="modal_ritiro_date = false"
                          >Annulla</v-btn
                        >
                        <v-btn
                          text
                          color="primary"
                          @click="$refs.dialog_ritiro_date.save(ritiro_date)"
                          >OK</v-btn
                        >
                      </v-date-picker>
                    </v-dialog>
                  </v-col>
                  <v-col cols="5">
                    <v-dialog
                      ref="dialog_ritiro_time"
                      v-model="modal_ritiro_time"
                      persistent
                      width="290px"
                    >
                      <template #activator="{ on, attrs }">
                        <v-text-field
                          v-model="ritiro_time"
                          label="Ora"
                          prepend-icon="mdi-clock-outline"
                          readonly
                          dense
                          v-bind="attrs"
                          v-on="on"
                        />
                      </template>
                      <v-time-picker
                        v-if="modal_ritiro_time"
                        v-model="ritiro_time"
                        full-width
                        format="24hr"
                        @click:hour="chiudi_ora_ritiro"
                      >
                        <v-spacer />
                        <v-btn
                          text
                          color="primary"
                          @click="modal_ritiro_time = false"
                          >Annulla</v-btn
                        >
                      </v-time-picker>
                    </v-dialog>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-actions>
                <v-btn
                  v-if="prenotazione.data_ritiro_ts"
                  text
                  x-small
                  color="error"
                  :loading="loading_ritiro"
                  @click="azzera_data_ritiro"
                >
                  Azzera
                </v-btn>
                <v-spacer />
                <v-btn text small @click="menu_ritiro = false">Annulla</v-btn>
                <v-btn
                  color="primary"
                  small
                  :loading="loading_ritiro"
                  :disabled="!ritiro_date || !ritiro_time"
                  @click="salva_data_ritiro"
                >
                  Salva
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
        </div>

        <!-- Articoli -->
        <div class="d-flex align-center mb-3">
          <span class="subtitle-1 font-weight-medium">Articoli</span>
          <v-spacer />
          <PrenotazioneAggiungiProdotto
            v-if="is_modificabile"
            ref="aggiungi_prodotto"
            :id-prenotazione="prenotazione._id.$oid"
            @aggiornata="on_aggiornata"
          >
            <template #activator="{ on }">
              <v-btn small color="primary" outlined v-on="on">
                <v-icon left small>mdi-plus</v-icon>
                Aggiungi prodotto
              </v-btn>
            </template>
          </PrenotazioneAggiungiProdotto>
        </div>

        <div v-for="art in articoli_display" :key="art.art">
          <!-- Header riga prodotto -->
          <v-row class="px-2 py-2" align="center">
            <v-col cols="2" class="d-flex align-center">
              <v-btn icon small @click="toggle_art(art.art)">
                <v-icon>{{
                  open_articoli[art.art] ? "mdi-chevron-up" : "mdi-chevron-down"
                }}</v-icon>
              </v-btn>
              <span class="ml-1 text-body-2 font-weight-bold">{{
                art.tot
              }}</span>
              <span
                v-if="qta_convertita_art(art.art)"
                class="caption grey--text font-weight-regular ml-1"
                >/ {{ art.tot + qta_convertita_art(art.art) }}</span
              >
            </v-col>
            <v-col cols="5">
              <div class="d-flex align-center">
                <v-avatar size="36" class="mr-3">
                  <v-img :src="avatar_img_by_art(art.art)" />
                </v-avatar>
                <div>
                  <div class="text-body-2 font-weight-medium">
                    {{ nome_prodotto_parts(art.nome)[0] }}
                  </div>
                  <div class="caption grey--text">
                    {{ nome_prodotto_parts(art.nome)[1] }}
                  </div>
                </div>
              </div>
            </v-col>
            <v-col cols="3" class="caption grey--text px-2 text-truncate">{{
              art.commento
            }}</v-col>
            <v-col cols="2" class="text-right">
              <PrenotazioneModificaColoriDialog
                v-if="is_modificabile"
                :id-prenotazione="prenotazione._id.$oid"
                :art="art"
                @aggiornata="on_aggiornata"
              >
                <template #activator="{ on }">
                  <v-btn icon v-on="on">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                </template>
              </PrenotazioneModificaColoriDialog>
              <v-btn
                v-if="is_modificabile"
                icon
                color="error"
                @click="elimina_prodotto(art.art)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-col>
          </v-row>

          <!-- Sezione colori + commento (espandibile) -->
          <template v-if="open_articoli[art.art]">
            <v-row no-gutters class="px-2 pb-3">
              <v-col cols="1" />
              <v-col cols="11">
                <v-row
                  v-for="colore in art.colori"
                  :key="colore.id"
                  no-gutters
                  align="center"
                  class="my-1"
                >
                  <v-col
                    cols="2"
                    class="text-body-2 font-weight-medium text-right pr-3"
                  >
                    {{ colore.numero }}
                    <span
                      v-if="qta_convertita_map[art.art + '__' + colore.id]"
                      class="caption grey--text font-weight-regular"
                    >
                      /
                      {{
                        colore.numero +
                        qta_convertita_map[art.art + "__" + colore.id]
                      }}
                    </span>
                  </v-col>
                  <v-col cols="1">
                    <v-avatar size="27" class="mx-1">
                      <v-img :src="color_img_by_art(art.art, colore.id)" />
                    </v-avatar>
                  </v-col>
                  <v-col cols="9" class="caption px-2">
                    <span v-if="colore.nome_serie" class="grey--text mr-1">{{
                      colore.nome_serie
                    }}</span>
                    {{ colore.nome }}
                  </v-col>
                </v-row>

                <div v-if="art.commento" class="caption grey--text mt-2">
                  {{ art.commento }}
                </div>
              </v-col>
            </v-row>
          </template>

          <v-divider />
        </div>

        <v-alert v-if="!articoli_display.length" type="info" text dense>
          Nessun articolo. Usa "Aggiungi prodotto" per iniziare.
        </v-alert>

        <!-- Errori -->
        <v-alert v-if="error" type="error" dense text class="mt-2">
          {{ error }}
        </v-alert>

        <!-- Storico conversioni -->
        <div
          v-if="
            prenotazione.storico_conversioni &&
            prenotazione.storico_conversioni.length
          "
          class="mt-4"
        >
          <div class="subtitle-2 mb-2 d-flex align-center">
            <v-icon small class="mr-2" color="grey">mdi-history</v-icon>
            Storico conversioni
          </div>
          <v-simple-table dense>
            <template #default>
              <thead>
                <tr>
                  <th class="caption">Data</th>
                  <th class="caption">Ordine</th>
                  <th class="caption">Riepilogo</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="conv in prenotazione.storico_conversioni"
                  :key="conv.id_ordine"
                >
                  <td class="caption">{{ format_conv_ts(conv.data_ts) }}</td>
                  <td class="caption">#{{ conv.numero_ordine }}</td>
                  <td class="caption">
                    <div
                      v-for="a in conv.articoli"
                      :key="a.id_prodotto"
                      class="my-1"
                    >
                      <v-row no-gutters>
                        <v-col cols="1">
                          <span class="font-weight-bold">
                            {{
                              (a.colori || []).reduce((s, c) => s + c.qta, 0)
                            }}
                          </span>
                        </v-col>
                        <v-col cols="11">
                          <span class="grey--text ml-1">{{
                            a.nome_prodotto
                          }}</span>
                        </v-col>
                      </v-row>
                    </div>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>

        <!-- Commento generale -->
        <v-textarea
          :value="prenotazione.commento"
          label="Commento"
          outlined
          dense
          rows="2"
          auto-grow
          hide-details
          class="mt-4"
          :readonly="!is_modificabile"
          @blur="salva_commento($event.target.value)"
        />
      </v-card-text>

      <!-- Azioni stato -->
      <v-divider />
      <v-card-actions class="justify-end">
        <template v-if="prenotazione.stato === 0">
          <v-btn
            color="error"
            outlined
            :loading="loading_elimina"
            @click="dialog_elimina = true"
          >
            Elimina
          </v-btn>
          <v-btn
            color="primary"
            outlined
            :loading="loading_stato"
            @click="cambia_stato(1)"
          >
            Conferma
          </v-btn>
        </template>
        <template v-if="prenotazione.stato === 1">
          <v-btn
            color="error"
            outlined
            :loading="loading_elimina"
            @click="dialog_elimina = true"
          >
            Elimina
          </v-btn>
          <v-btn
            outlined
            color="grey"
            :loading="loading_stato"
            @click="cambia_stato(0)"
          >
            Riporta in bozza
          </v-btn>

          <ConvertOrdineDialog
            :prenotazione="prenotazione"
            @convertita="on_convertita"
          >
            <template #activator="{ on }">
              <v-btn outlined class="ml-2" color="success" v-on="on">
                Converti in ordine
              </v-btn>
            </template>
          </ConvertOrdineDialog>
        </template>
        <template v-if="prenotazione.stato === 2 || prenotazione.stato === 3">
          <v-btn
            color="error"
            outlined
            :loading="loading_elimina"
            @click="dialog_elimina = true"
          >
            Elimina definitivamente
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>

    <!-- Dialog conferma eliminazione -->
    <v-dialog v-model="dialog_elimina" max-width="400">
      <v-card>
        <v-card-title>
          {{
            prenotazione.stato === 0
              ? "Elimina prenotazione"
              : prenotazione.stato === 1
              ? "Annulla prenotazione"
              : "Elimina definitivamente"
          }}
        </v-card-title>
        <v-card-text>
          {{
            prenotazione.stato === 0
              ? "La prenotazione verrà eliminata definitivamente."
              : prenotazione.stato === 1
              ? "La prenotazione verrà eliminata definitivamente. Questa operazione non è reversibile."
              : "La prenotazione verrà rimossa definitivamente dall'archivio. Questa operazione non è reversibile."
          }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog_elimina = false">No</v-btn>
          <v-btn color="error" :loading="loading_elimina" @click="elimina"
            >Sì</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import StatoBadge from "@/components/Prenotazioni/common/StatoBadge.vue";
import printer from "@/service/printer.js";
import ConvertOrdineDialog from "./ConvertOrdineDialog.vue";
import PrenotazioneAggiungiProdotto from "./PrenotazioneAggiungiProdotto.vue";
import PrenotazioneModificaColoriDialog from "./PrenotazioneModificaColoriDialog.vue";
import moment from "moment";

export default {
  name: "PrenotazioneDettaglio",

  components: {
    StatoBadge,
    ConvertOrdineDialog,
    PrenotazioneAggiungiProdotto,
    PrenotazioneModificaColoriDialog,
  },

  props: {
    value: { type: Boolean, default: false },
    prenotazione: { type: Object, required: true },
  },

  data() {
    return {
      loading_stato: false,
      loading_elimina: false,
      dialog_elimina: false,
      error: null,
      open_articoli: {},
      menu_ritiro: false,
      modal_ritiro_date: false,
      modal_ritiro_time: false,
      ritiro_date: "",
      ritiro_time: "09:00",
      loading_ritiro: false,
    };
  },

  computed: {
    is_modificabile() {
      return this.prenotazione.stato === 0 || this.prenotazione.stato === 1;
    },

    ritiro_date_label() {
      if (!this.ritiro_date) return "";
      moment.locale("it");
      return moment(this.ritiro_date).format("dddd D MMMM");
    },

    min_ritiro_date() {
      return moment().format("YYYY-MM-DD");
    },

    prodotti_map() {
      const map = {};
      (this.$store.getters.get_lista_prodotti_raw || []).forEach((p) => {
        map[p.id] = p;
      });
      return map;
    },

    // Mappa { "id_prodotto__id_colore" → qta_totale_convertita }
    // Usata per mostrare "residuo / originale" nella riga colore
    qta_convertita_map() {
      const map = {};
      for (const conv of this.prenotazione.storico_conversioni || []) {
        for (const art of conv.articoli || []) {
          for (const c of art.colori || []) {
            const key = art.id_prodotto + "__" + c.id_colore;
            map[key] = (map[key] || 0) + c.qta;
          }
        }
      }
      return map;
    },

    // Lista articoli da visualizzare: unione di articoli correnti + articoli/colori
    // rimossi dopo conversioni (ricostruiti dallo storico con numero=0)
    articoli_display() {
      const attuali = {};
      for (const art of this.prenotazione.articoli || []) {
        attuali[art.art] = art;
      }

      // Raccoglie tutti gli articoli e colori menzionati nello storico
      const storici = {};
      for (const conv of this.prenotazione.storico_conversioni || []) {
        for (const art of conv.articoli || []) {
          if (!storici[art.id_prodotto]) {
            storici[art.id_prodotto] = { nome: art.nome_prodotto, colori: {} };
          }
          for (const c of art.colori || []) {
            if (!storici[art.id_prodotto].colori[c.id_colore]) {
              storici[art.id_prodotto].colori[c.id_colore] = {
                nome: c.nome_colore,
                nome_serie: c.nome_serie || "",
              };
            }
          }
        }
      }

      const risultato = [];

      // Articoli correnti: integra eventuali colori rimossi presenti nello storico
      for (const art of this.prenotazione.articoli || []) {
        const colori = [...(art.colori || [])];
        const colori_ids = new Set(colori.map((c) => c.id));
        if (storici[art.art]) {
          for (const [id_colore, info] of Object.entries(
            storici[art.art].colori
          )) {
            if (!colori_ids.has(id_colore)) {
              colori.push({
                id: id_colore,
                nome: info.nome,
                nome_serie: info.nome_serie,
                numero: 0,
              });
            }
          }
        }
        risultato.push({ ...art, colori });
      }

      // Articoli fantasma: completamente convertiti e rimossi da prenotazione.articoli
      for (const [id_prodotto, info] of Object.entries(storici)) {
        if (!attuali[id_prodotto]) {
          const colori = Object.entries(info.colori).map(([id_colore, c]) => ({
            id: id_colore,
            nome: c.nome,
            nome_serie: c.nome_serie,
            numero: 0,
          }));
          risultato.push({
            art: id_prodotto,
            nome: info.nome,
            tot: 0,
            colori,
            commento: "",
          });
        }
      }

      return risultato;
    },
  },

  mounted() {
    document.addEventListener("keydown", this.on_keydown);
  },

  beforeDestroy() {
    document.removeEventListener("keydown", this.on_keydown);
  },

  methods: {
    on_keydown(e) {
      if (!this.value || !this.is_modificabile) return;
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.key === "/") {
        e.preventDefault();
        if (this.$refs.aggiungi_prodotto) {
          this.$refs.aggiungi_prodotto.dialog = true;
        }
      }
    },

    async cambia_stato(stato) {
      this.loading_stato = true;
      this.error = null;
      try {
        await this.$store.dispatch("prenotazioni/cambia_stato", {
          id: this.prenotazione._id.$oid,
          stato,
        });
      } catch {
        this.error = "Impossibile cambiare lo stato. Riprova.";
      } finally {
        this.loading_stato = false;
      }
    },

    async elimina() {
      this.loading_elimina = true;
      this.error = null;
      try {
        await this.$store.dispatch(
          "prenotazioni/elimina",
          this.prenotazione._id.$oid
        );
        this.dialog_elimina = false;
        this.$emit("input", false);
      } catch (e) {
        this.dialog_elimina = false;
        this.error = "Errore durante l'eliminazione. Riprova.";
      } finally {
        this.loading_elimina = false;
      }
    },

    async salva_commento(commento) {
      if (commento === this.prenotazione.commento) return;
      try {
        await this.$store.dispatch("prenotazioni/aggiorna_commento", {
          id: this.prenotazione._id.$oid,
          commento,
        });
      } catch {
        this.error = "Errore salvataggio commento.";
      }
    },

    async elimina_prodotto(id_prodotto) {
      try {
        await this.$store.dispatch("prenotazioni/elimina_prodotto", {
          id: this.prenotazione._id.$oid,
          id_prodotto,
        });
      } catch {
        this.error = "Errore durante la rimozione dell'articolo.";
      }
    },

    on_aggiornata() {
      // La mutation update nel store aggiorna la prop reactively tramite computed
    },

    toggle_art(id) {
      this.$set(this.open_articoli, id, !this.open_articoli[id]);
    },

    nome_prodotto_parts(nome) {
      return nome ? nome.split(" in ") : ["", ""];
    },

    avatar_img_by_art(art_id) {
      const p = this.prodotti_map[art_id];
      if (!p) return "";
      return (
        process.env.VUE_APP_API_ROOT +
        "/avatar_img/" +
        art_id +
        (p.avatar_md5 ? "?t=" + p.avatar_md5 : "")
      );
    },

    qta_convertita_art(art_id) {
      let tot = 0;
      for (const [key, qta] of Object.entries(this.qta_convertita_map)) {
        if (key.startsWith(art_id + "__")) tot += qta;
      }
      return tot;
    },

    format_conv_ts(ts) {
      return moment.unix(ts).format("DD/MM/YYYY HH:mm");
    },

    conv_riepilogo(conv) {
      return (conv.articoli || [])
        .map((a) => {
          const tot = (a.colori || []).reduce((s, c) => s + c.qta, 0);
          return `${a.nome_prodotto} ${tot} pz`;
        })
        .join(" · ");
    },

    color_img_by_art(art_id, colore_id) {
      const p = this.prodotti_map[art_id];
      if (p?.specie)
        return process.env.VUE_APP_API_ROOT + "/specie/img_colore/" + colore_id;
      return (
        process.env.VUE_APP_API_ROOT +
        "/prodotto/" +
        art_id +
        "/img_colore/" +
        colore_id
      );
    },

    init_ritiro_picker() {
      if (this.prenotazione.data_ritiro_ts) {
        this.ritiro_date = moment
          .unix(this.prenotazione.data_ritiro_ts)
          .format("YYYY-MM-DD");
        this.ritiro_time = moment
          .unix(this.prenotazione.data_ritiro_ts)
          .format("HH:mm");
      } else {
        this.ritiro_date = moment().add(1, "day").format("YYYY-MM-DD");
        this.ritiro_time = "09:00";
      }
    },

    chiudi_ora_ritiro(v) {
      this.ritiro_time = (v < 10 ? "0" + v : v) + ":00";
      this.modal_ritiro_time = false;
    },

    async salva_data_ritiro() {
      const ts = parseInt(
        moment(this.ritiro_date + " " + this.ritiro_time).format("X")
      );
      this.loading_ritiro = true;
      this.error = null;
      try {
        await this.$store.dispatch("prenotazioni/aggiorna_data_ritiro", {
          id: this.prenotazione._id.$oid,
          ts,
        });
        this.menu_ritiro = false;
      } catch {
        this.error = "Errore aggiornamento data ritiro.";
      } finally {
        this.loading_ritiro = false;
      }
    },

    async azzera_data_ritiro() {
      this.loading_ritiro = true;
      this.error = null;
      try {
        await this.$store.dispatch("prenotazioni/aggiorna_data_ritiro", {
          id: this.prenotazione._id.$oid,
          ts: null,
        });
        this.menu_ritiro = false;
      } catch {
        this.error = "Errore azzeramento data ritiro.";
      } finally {
        this.loading_ritiro = false;
      }
    },

    stampa_pdf() {
      printer.print_pdf_prenotazione(this.prenotazione._id.$oid);
    },

    on_convertita() {
      this.$emit("input", false);
    },
  },
};
</script>
